// src/components/ScreenerCard.tsx
import React, { useState, useEffect } from "react";
import type { AnalyzeScreenerResponse } from "../api/myTypes";
import TickerPrice from "./TickerPrice";
import { buildApiUrl } from "../api/etfApi";

interface ScreenerCardProps {
  data: AnalyzeScreenerResponse;
}

const ScreenerCard: React.FC<ScreenerCardProps> = ({ data }) => {
  const p = data.asset_profile || {};
  const dt = new Date(data.generated_at);

  // Extract initial prices from price_context (from analysis response)
  const getInitialPrices = () => {
    let currentPrice: number | null = null;
    let previousPrice: number | null = null;
    
    if (p.price_context && p.price_context.candles && Array.isArray(p.price_context.candles)) {
      const candles = p.price_context.candles;
      if (candles.length > 0) {
        // Current price is the last candle's close (latest available from analysis)
        currentPrice = candles[candles.length - 1]?.close || null;
        // Previous price is the second-to-last candle's close
        if (candles.length > 1) {
          previousPrice = candles[candles.length - 2]?.close || null;
        }
      }
    }
    return { currentPrice, previousPrice };
  };

  const [currentPrice, setCurrentPrice] = useState<number | null>(getInitialPrices().currentPrice);
  const [previousPrice, setPreviousPrice] = useState<number | null>(getInitialPrices().previousPrice);
  const [preMarketPrice, setPreMarketPrice] = useState<number | null>(null);
  const [preMarketChange, setPreMarketChange] = useState<number | null>(null);
  const [postMarketPrice, setPostMarketPrice] = useState<number | null>(null);
  const [postMarketChange, setPostMarketChange] = useState<number | null>(null);
  const [isPrePostMarket, setIsPrePostMarket] = useState<boolean>(false);
  const [marketStatus, setMarketStatus] = useState<string>("Market Closed");

  // Update market status on mount and periodically
  useEffect(() => {
    const getMarketStatus = () => {
      const now = new Date();
      const etTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const dayOfWeek = etTime.getDay(); // 0 = Sunday, 6 = Saturday
      const hours = etTime.getHours();
      const minutes = etTime.getMinutes();
      const timeInMinutes = hours * 60 + minutes;
      
      // Markets are closed on weekends (Saturday = 6, Sunday = 0)
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return "Market Closed";
      }
      
      // Pre-market: 4:00 AM - 9:30 AM ET (240-570 minutes) - Monday through Friday only
      // Regular market: 9:30 AM - 4:00 PM ET (570-960 minutes) - Monday through Friday only
      // Post-market: 4:00 PM - 8:00 PM ET (960-1200 minutes) - Monday through Friday only
      if (timeInMinutes >= 240 && timeInMinutes < 570) {
        return "Pre-Market";
      } else if (timeInMinutes >= 570 && timeInMinutes < 960) {
        return "Market Open";
      } else if (timeInMinutes >= 960 && timeInMinutes < 1200) {
        return "Post-Market";
      } else {
        return "Market Closed";
      }
    };

    const updateMarketStatus = () => {
      const status = getMarketStatus();
      setMarketStatus(status);
    };

    // Initial update
    updateMarketStatus();

    // Update every minute to catch status changes
    const statusInterval = setInterval(updateMarketStatus, 60000);

    return () => {
      clearInterval(statusInterval);
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Periodically fetch latest price from data provider (not LLM)
  useEffect(() => {
    const fetchCurrentPrice = async () => {
      try {
        const url = buildApiUrl(`api/current-price/${data.ticker}`);
        const response = await fetch(url);
        if (response.ok) {
          const priceData = await response.json();
          // Check if we're currently in normal market hours (9:30 AM - 4:00 PM ET)
          const now = new Date();
          const etTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
          const dayOfWeek = etTime.getDay();
          const hours = etTime.getHours();
          const minutes = etTime.getMinutes();
          const timeInMinutes = hours * 60 + minutes;
          const isNormalMarketHours = (dayOfWeek !== 0 && dayOfWeek !== 6) && 
                                      (timeInMinutes >= 570 && timeInMinutes < 960);
          
          if (priceData.current_price) {
            // Always use API's previous_close as comparison baseline (authoritative for live price)
            if (priceData.previous_close != null && priceData.previous_close > 0) {
              setPreviousPrice(priceData.previous_close);
            } else if (previousPrice === null || previousPrice === undefined) {
              const initialPrev = getInitialPrices().previousPrice;
              setPreviousPrice(initialPrev || priceData.current_price);
            }
            
            // Primary price logic:
            // - During market hours: use current_price (live market price)
            // - Outside market hours: use previous_close (last market close)
            // The backend now handles this, but we ensure consistency here
            if (isNormalMarketHours) {
              // During market hours, use the current_price from API (live market price)
              setCurrentPrice(priceData.current_price);
            } else {
              // Outside market hours, use previous_close (last market close)
              // This ensures the primary price shows last close until market opens
              if (priceData.previous_close) {
                setCurrentPrice(priceData.previous_close);
              } else {
                // Fallback to current_price if previous_close not available
                setCurrentPrice(priceData.current_price);
              }
            }
          }
          
          // Update pre/post-market data if available
          const hasPreMarketPrice = priceData.pre_market_price !== null && 
                                   priceData.pre_market_price !== undefined && 
                                   !isNaN(priceData.pre_market_price);
          const hasPostMarketPrice = priceData.post_market_price !== null && 
                                     priceData.post_market_price !== undefined && 
                                     !isNaN(priceData.post_market_price);
          const isPrePostMarket = priceData.is_pre_post_market === true || priceData.is_pre_post_market === "true" || priceData.is_pre_post_market === 1;
          
          // Determine if we're in pre-market hours (4:00 AM - 9:30 AM ET)
          const isPreMarketHours = (dayOfWeek !== 0 && dayOfWeek !== 6) && (timeInMinutes >= 240 && timeInMinutes < 570);
          // Determine if we're in post-market hours (4:00 PM - 8:00 PM ET)
          const isPostMarketHours = (dayOfWeek !== 0 && dayOfWeek !== 6) && (timeInMinutes >= 960 && timeInMinutes < 1200);
          
          // Show pre-market price if:
          // 1. We have pre-market data from API
          // 2. We're in pre-market hours (4:00 AM - 9:30 AM ET)
          // 3. We're NOT in normal market hours (9:30 AM - 4:00 PM)
          const shouldShowPreMarket = hasPreMarketPrice && !isNormalMarketHours && isPreMarketHours;
          
          // Show post-market price if:
          // 1. We have post-market data from API
          // 2. We're in post-market hours (4:00 PM - 8:00 PM ET) OR API indicates post-market
          // 3. We're NOT in normal market hours (9:30 AM - 4:00 PM)
          const shouldShowPostMarket = hasPostMarketPrice && !isNormalMarketHours && (isPostMarketHours || (isPrePostMarket && !isPreMarketHours));
          
          if (shouldShowPreMarket) {
            setPreMarketPrice(priceData.pre_market_price);
            setPreMarketChange(priceData.pre_market_change !== null && priceData.pre_market_change !== undefined ? priceData.pre_market_change : null);
            setPostMarketPrice(null);
            setPostMarketChange(null);
            setIsPrePostMarket(true);
          } else if (shouldShowPostMarket) {
            setPreMarketPrice(null);
            setPreMarketChange(null);
            setPostMarketPrice(priceData.post_market_price);
            setPostMarketChange(priceData.post_market_change !== null && priceData.post_market_change !== undefined ? priceData.post_market_change : null);
            setIsPrePostMarket(true);
          } else {
            // Clear all pre/post-market data if not available OR if we're in normal market hours
            setPreMarketPrice(null);
            setPreMarketChange(null);
            setPostMarketPrice(null);
            setPostMarketChange(null);
            setIsPrePostMarket(false);
          }
        }
      } catch (error) {
        // Silently fail - keep showing the price from analysis
        console.debug("Failed to fetch current price:", error);
      }
    };

    // Helper function to check if we're in market hours or pre/post market hours
    const isInTradingHours = () => {
      const now = new Date();
      const etTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const dayOfWeek = etTime.getDay(); // 0 = Sunday, 6 = Saturday
      const hours = etTime.getHours();
      const minutes = etTime.getMinutes();
      const timeInMinutes = hours * 60 + minutes;
      
      // Markets are closed on weekends
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return false;
      }
      
      // Pre-market: 4:00 AM - 9:30 AM ET (240-570 minutes) - Monday through Friday only
      // Regular market: 9:30 AM - 4:00 PM ET (570-960 minutes) - Monday through Friday only
      // Post-market: 4:00 PM - 8:00 PM ET (960-1200 minutes) - Monday through Friday only
      const isPreMarket = timeInMinutes >= 240 && timeInMinutes < 570;
      const isRegularMarket = timeInMinutes >= 570 && timeInMinutes < 960;
      const isPostMarket = timeInMinutes >= 960 && timeInMinutes < 1200;
      
      return isPreMarket || isRegularMarket || isPostMarket;
    };

    // Fetch immediately after component mounts (to get latest price after analysis)
    fetchCurrentPrice();

    // Only set up periodic updates if we're currently in trading hours
    // If outside trading hours, only fetch once and don't set up periodic updates
    let interval: ReturnType<typeof setInterval> | null = null;
    
    if (isInTradingHours()) {
      // We're in trading hours, set up periodic updates
      // Update every 30 seconds during market hours or pre/post market
      interval = setInterval(() => {
        // Re-check if we're still in trading hours before each fetch
        // This handles the case where we cross into/out of trading hours
        if (isInTradingHours()) {
          fetchCurrentPrice();
        } else {
          // We've moved outside trading hours, clear the interval
          if (interval) {
            clearInterval(interval);
            interval = null;
          }
        }
      }, 30000); // Check every 30 seconds
    } else {
      // Outside trading hours - only fetch once, no periodic updates
      console.log("Outside trading hours - price will only update once on mount");
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [data.ticker]); // Remove currentPrice from dependencies - we don't want to re-run when price updates

  return (
    <div className="card etf-card">
      <div className="card-header-row">
        <div>
          <div className="etf-ticker">
            {data.ticker}
            <TickerPrice 
              currentPrice={currentPrice} 
              previousPrice={previousPrice}
              preMarketPrice={preMarketPrice}
              preMarketChange={preMarketChange}
              postMarketPrice={postMarketPrice}
              postMarketChange={postMarketChange}
              isPrePostMarket={isPrePostMarket}
            />
          </div>
          <div 
            style={{
              fontSize: "0.65rem",
              color: marketStatus === "Market Open" ? "#22c55e" : 
                     marketStatus === "Pre-Market" ? "#f59e0b" :
                     marketStatus === "Post-Market" ? "#3b82f6" : "#6b7280",
              fontWeight: 500,
              marginTop: "4px",
              marginBottom: "2px",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              display: "block",
              lineHeight: "1.2",
              minHeight: "14px",
              visibility: "visible",
              opacity: 1,
              width: "100%"
            }}
            data-testid="market-status-indicator"
          >
            {marketStatus || "Market Closed"}
          </div>
          {p.name && <div className="etf-name">{p.name}</div>}
        </div>
        <div className="pill pill-mode">
          {data.chart_interval} • Screener
        </div>
      </div>

      <div className="card-meta-row">
        <span className="meta-pill">Screener: {data.screener}</span>
        {p.category && <span className="meta-pill">{p.category}</span>}
        {p.sector && <span className="meta-pill">{p.sector}</span>}
        {p.region && <span className="meta-pill">{p.region}</span>}
      </div>

      <div className="card-footer-row">
        <span className="meta-muted">
          Generated {dt.toLocaleString(undefined, { hour12: false })}
        </span>
        {data.data_provider && (
          <span className="meta-muted" style={{ marginLeft: "12px", fontSize: "0.75rem", opacity: 0.7 }}>
            Data: {data.data_provider}
          </span>
        )}
      </div>
    </div>
  );
};

export default ScreenerCard;




